'use client';

import { Minus, Plus, Trash2 } from 'lucide-react';
import type React from 'react';
import Image from 'next/image';
import { useDictionary } from '@/core/hooks/use-dictionary';
import type { CartItem as CartItemType } from '@/core/types/cart';
import { formatPrice } from '@/utils/formatUtils';
import { Heading } from '@/components/ui/Heading';
import { useCartStore } from '@/core/hooks/cart/useCartStore';

interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (productId: number, quantity: number) => void;
  onRemove: (productId: number) => void;
}

export const CartItem: React.FC<CartItemProps> = ({
  item,
  onUpdateQuantity,
  onRemove,
}) => {
  const { dictionary } = useDictionary();
  const { product_info, quantity } = item;
  const { isCartLoading } = useCartStore();

  const handleQuantityChange = (delta: number) => {
    if (isCartLoading) return;
    const newQuantity = quantity + delta;
    if (newQuantity > 0) {
      //@ts-ignore
      onUpdateQuantity(product_info.id, newQuantity);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-xs">
      {/* Product Image */}
      <div className="flex-shrink-0 relative aspect-square min-h-32">
        <Image
          src={product_info?.thumbnail?.url || product_info?.thumbnail_url || ""}
          fill
          alt={product_info.title}
          className="rounded-xs object-cover"
        />
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-2">
          <div className="flex flex-col space-y-3">
            <Heading level={3}>
              {product_info.title} {product_info.color_title}
            </Heading>
            <div>
              <p className="text-sm text-primary-500">
                {dictionary.forms.code}
                {' : '}
                {product_info.code}
              </p>
            </div>
          </div>
          {/* Remove Button */}
          <button
            //@ts-ignore
            onClick={() => !isCartLoading && onRemove(product_info.id)}
            disabled={isCartLoading}
            className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
          >
            <Trash2 size={14} />
          </button>
        </div>
        <div className="border-t-1 border-gray-250 w-full my-3"></div>

        {/* Quantity and Price */}
        <div className="flex justify-between items-center">
          <div className="flex gap-3 items-center">
            <div className="flex items-center gap-3 bg-white rounded-xs p-2">
              {quantity > 1 ? (
                <button
                  onClick={() => handleQuantityChange(-1)}
                  disabled={isCartLoading}
                  className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50"
                >
                  <Minus size={16} />
                </button>
              ) : (
                <button
                  //@ts-ignore
                  onClick={() => !isCartLoading && onRemove(product_info.id)}
                  disabled={isCartLoading}
                  className="p-1 rounded-full text-red-500 hover:bg-red-100 disabled:opacity-50"
                >
                  <Trash2 size={16} />
                </button>
              )}

              <span className="font-primary-bold min-w-[2rem] text-center">
                {quantity}
              </span>

              <button
                onClick={() => handleQuantityChange(1)}
                disabled={isCartLoading || quantity >= product_info.inventory}
                className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="space-y-1">
              {product_info.discount_percent > 0 ? (
                <div>
                  <div className="text-xs text-red-500 line-through">
                    {formatPrice(Number(product_info.price))}{' '}
                    {dictionary.ui.product.currency}
                  </div>
                  <div className="text-primary-600 text-xs">
                    {formatPrice(Number(product_info.final_price))}{' '}
                    {dictionary.ui.product.currency}
                  </div>
                </div>
              ) : (
                <div className="text-primary-600 text-xs">
                  {formatPrice(Number(product_info.price))}{' '}
                  {dictionary.ui.product.currency}
                </div>
              )}
            </div>
          </div>
          {/* total price */}
          <div className="text-end space-y-1">
            <div className="font-primary-bold text-sm text-primary-900">
              {formatPrice(
                (Number(product_info.final_price) || 0) * quantity
              )}{' '}
              {dictionary.ui.product.currency}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
